import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import { I18nProvider } from "./context/I18nContext";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { useTracking } from "./hooks/useTracking";
import { HreflangTags } from "./components/HreflangTags";
import { SEOHead } from "./components/SEOHead";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AccountPage from "./pages/AccountPage";
import SuccessPage from "./pages/SuccessPage";
import AdminPage from "./pages/AdminPage";
import ComparisonPage from "./pages/ComparisonPage";
import BlogPage from "./pages/BlogPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import MarketplacePage from "./pages/MarketplacePage";
import ModuleDetailPage from "./pages/ModuleDetailPage";
import SubmitModulePage from "./pages/SubmitModulePage";
import AuthCallback from "./pages/AuthCallback";
import DownloadsPage from "./pages/DownloadsPage";
import FAQPage from "./pages/FAQPage";
import ChangelogPage from "./pages/ChangelogPage";
import PricingPage from "./pages/PricingPage";
import LegalPage from "./pages/LegalPage";
import ReferralRulesPage from "./pages/ReferralRulesPage";
import ResourcesPage from "./pages/ResourcesPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate('/login', { replace: true });
  }, [user, loading, navigate]);

  if (loading) return null;
  return user ? children : null;
}

function TrackingWrapper({ children }) {
  useTracking();
  return children;
}

/** Keep ?lang= in URL during React Router navigation */
function LangSync() {
  const location = useLocation();
  useLayoutEffect(() => {
    const savedLang = localStorage.getItem('hexopdf_lang');
    if (!savedLang) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('lang') !== savedLang) {
      url.searchParams.set('lang', savedLang);
      window.history.replaceState(null, '', url.toString());
    }
  }, [location.pathname, location.key]);
  return null;
}

function AppContent() {
  const { theme } = useTheme();
  return (
    <>
      <div className="hex-bg" aria-hidden="true" />
      <BrowserRouter>
        <HreflangTags />
        <SEOHead />
        <LangSync />
        <TrackingWrapper>
          <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/success" element={<SuccessPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
          <Route path="/marketplace/submit" element={<ProtectedRoute><SubmitModulePage /></ProtectedRoute>} />
          <Route path="/marketplace/:slug" element={<ProtectedRoute><ModuleDetailPage /></ProtectedRoute>} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/changelog" element={<ChangelogPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/referral-rules" element={<ReferralRulesPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/compare/adobe" element={<ComparisonPage competitor="adobe" />} />
          <Route path="/compare/foxit" element={<ComparisonPage competitor="foxit" />} />
          </Routes>
        </TrackingWrapper>
      </BrowserRouter>
      <Toaster position="top-right" theme={theme} />
    </>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <CurrencyProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </CurrencyProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
